import { apiGet, apiGetPaginated, apiPost, apiPatch, apiDelete } from './client';
import type {
  Organization,
  CreateOrganizationFormData,
  OrganizationRole,
  PaginatedResult,
} from '@/types';
import {
  transformOrganization,
  transformOrganizationMember,
  type ApiOrganization,
  type ApiOrganizationMember,
} from './organization-transform';

export const organizationsApi = {
  listPage: async (
    query: { page?: number; pageSize?: number; search?: string } = {}
  ): Promise<PaginatedResult<Organization[]>> => {
    const result = await apiGetPaginated<ApiOrganization[]>('/api/organizations', {
      params: query,
    });
    return { data: result.data.map(transformOrganization), pagination: result.pagination };
  },

  // List organizations
  list: async (): Promise<Organization[]> => {
    return (await organizationsApi.listPage({ pageSize: 100 })).data;
  },

  // Slug availability (excludeId ignores the organization being edited)
  checkSlug: (slug: string, excludeId?: string) =>
    apiGet<{ slug: string; valid: boolean; available: boolean }>('/api/organizations/check-slug', {
      params: { slug, excludeId },
    }),

  // Get organization by ID
  get: async (id: string): Promise<Organization> => {
    const data = await apiGet<ApiOrganization>(`/api/organizations/${id}`);
    return transformOrganization(data);
  },

  // Create organization
  create: async (data: CreateOrganizationFormData): Promise<Organization> => {
    const result = await apiPost<ApiOrganization>('/api/organizations', data);
    return transformOrganization(result);
  },

  // Update organization
  update: async (
    id: string,
    data: Partial<CreateOrganizationFormData> & { isActive?: boolean }
  ): Promise<Organization> => {
    const result = await apiPatch<ApiOrganization>(`/api/organizations/${id}`, data);
    return transformOrganization(result);
  },

  // Delete organization
  delete: (id: string) => apiDelete<void>(`/api/organizations/${id}`),

  // List members
  listMembers: async (organizationId: string) => {
    const members = await apiGet<ApiOrganizationMember[]>(
      `/api/organizations/${organizationId}/members`
    );
    return members.map((member) => transformOrganizationMember(member, organizationId));
  },

  // Add member
  addMember: async (organizationId: string, data: { email: string; role: OrganizationRole }) =>
    transformOrganizationMember(
      await apiPost<ApiOrganizationMember>(`/api/organizations/${organizationId}/members`, data),
      organizationId
    ),

  // Update member role
  updateMemberRole: async (organizationId: string, userId: string, role: OrganizationRole) =>
    transformOrganizationMember(
      await apiPatch<ApiOrganizationMember>(
        `/api/organizations/${organizationId}/members/${userId}`,
        { role }
      ),
      organizationId
    ),

  // Remove member
  removeMember: (organizationId: string, userId: string) =>
    apiDelete<void>(`/api/organizations/${organizationId}/members/${userId}`),
};
